import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { TicketView } from '../../../shared/models/ticket.model';
import { TicketUiModel } from '../../../shared/ui-models/ticket.ui-model';
import { getTickets } from '../../../shared/services/data/ticket.data';
import { APP_ROUTES } from '../../../shared/config/routes-config';
import { RouteConfig } from '../../../shared/interfaces/routes-config.interface';
import { LoadingData } from '../../../ui/LoadingData';
import { NotFoundData } from '../../../ui/NotFoundData';
import {
  describeLoadError,
  LoadError
} from '../../../shared/components/core-ui/load-error/load-error.component';
import Button, { ButtonType } from '../../../shared/components/core-ui/button/button.component';
import { RaiseTicketModal } from '../../../shared/components/group-ui/raise-ticket/raise-ticket-modal.component';
import { CHIP, PRIORITY_STYLE, stateStyle, titleCase } from '../../shared/ticket-visuals';
import { sortQueue } from '../../shared/ticket-queue.util';

const TH = 'whitespace-nowrap px-3 py-3 font-gilroy-bold text-neutral-500';
const TD = 'border-t border-gray-100 px-3 py-3 whitespace-nowrap';

/**
 * Four queues, matching how people actually come looking: what I hold, what nobody
 * holds yet, everything open in my teams, and what I raised elsewhere. The first
 * three read Zammad overviews, so a new ticket shows up at once. "Raised by me"
 * reads search, which can lag a few seconds behind.
 */
const VIEWS: { key: TicketView; label: string }[] = [
  { key: 'mine', label: 'Mine' },
  { key: 'unassigned', label: 'Unassigned' },
  { key: 'team', label: 'My teams' },
  { key: 'raised', label: 'Raised by me' }
];

const COLUMNS = ['#', 'Priority', 'Title', 'Category', 'Team', 'Owner', 'State', 'Updated'];

export const TicketsQueueContainer: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  // The tab lives in the URL so "back" from a ticket returns to the same queue.
  const view: TicketView =
    (VIEWS.find((v) => v.key === new URLSearchParams(location.search).get('view'))
      ?.key as TicketView) ?? 'mine';

  const [tickets, setTickets] = useState<TicketUiModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    return getTickets(view).then((result) => {
      setLoadError(isErrorInfo(result) ? describeLoadError(result) : undefined);
      setTickets(isErrorInfo(result) ? [] : result);
      setLoading(false);
    });
  }, [view]);

  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = needle
      ? tickets.filter((t: TicketUiModel) =>
          [
            t.number,
            t.title,
            t.categoryLabel,
            t.categoryCode,
            t.team,
            t.ownerName,
            t.reservationRef
          ]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(needle))
        )
      : tickets;
    return sortQueue(filtered);
  }, [tickets, search]);

  const detailsPath = (id: number) =>
    (APP_ROUTES.TICKETS_MODULE.CHILDREN as RouteConfig).DETAILS.FULL_PATH.replace(
      ':ticketId',
      String(id)
    );

  return (
    <div className="flex h-full flex-col gap-5 md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[3rem]">
      <section className="flex flex-wrap items-center gap-3">
        <span className="h-8 w-1.5 rounded-full bg-secondary-500" aria-hidden="true" />
        <h1 className="font-gilroy-bold text-2xl text-neutral-500">Tickets</h1>
        {!loading && !loadError ? (
          <span className={`${CHIP} bg-primary-50 text-primary-700`}>
            {tickets.length} ticket{tickets.length === 1 ? '' : 's'}
          </span>
        ) : null}
        <span className="ml-auto">
          <Button type={ButtonType.primary} onClick={() => setShowCreate(true)}>
            Raise ticket
          </Button>
        </span>
      </section>

      <section className="flex flex-wrap items-center gap-3 rounded-2xl border border-gray-200 bg-white p-3 shadow-md">
        <div className="flex flex-wrap gap-1" role="tablist">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              role="tab"
              aria-selected={v.key === view}
              className={`rounded-lg px-3 py-1.5 text-sm font-gilroy-medium ${
                v.key === view
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-neutral-400 hover:bg-creams-100'
              }`}
              onClick={() => history.replace({ search: `?view=${v.key}` })}
            >
              {v.label}
            </button>
          ))}
        </div>
        <input
          className="ml-auto w-full rounded-lg border border-gray-200 px-3 py-2 text-sm sm:w-72"
          placeholder="Filter by number, title, team, reservation"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </section>

      <section>
        {loading ? (
          <LoadingData />
        ) : loadError ? (
          <LoadError message={loadError} onRetry={load} />
        ) : shown.length === 0 ? (
          <NotFoundData />
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-md">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="bg-creams-200">
                  {COLUMNS.map((column: string) => (
                    <th key={column} className={TH}>
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((ticket: TicketUiModel) => (
                  <tr
                    key={ticket.id}
                    className="cursor-pointer hover:bg-creams-100"
                    onClick={() => history.push(detailsPath(ticket.id))}
                  >
                    <td className={`${TD} text-neutral-300`}>{ticket.number}</td>
                    <td className={TD}>
                      <span className="flex items-center gap-1.5">
                        <span className={`${CHIP} ${PRIORITY_STYLE[ticket.priority]}`}>
                          {ticket.priority}
                        </span>
                        {ticket.emergency ? (
                          <span className={`${CHIP} bg-secondary-500 text-white`}>Emergency</span>
                        ) : null}
                      </span>
                    </td>
                    <td
                      className={`${TD} max-w-[320px] truncate font-gilroy-medium text-neutral-500`}
                    >
                      {ticket.title}
                    </td>
                    <td className={`${TD} text-neutral-400`} title={ticket.categoryLabel}>
                      {ticket.categoryCode ?? (
                        // A guest email arrives without one; someone in the team sets it.
                        <span className={`${CHIP} bg-creams-600 text-creams-900`}>
                          Needs category
                        </span>
                      )}
                    </td>
                    <td className={TD}>{ticket.team}</td>
                    <td className={TD}>
                      {ticket.ownerName ?? (
                        <span className={`${CHIP} bg-creams-500 text-creams-900`}>Unassigned</span>
                      )}
                    </td>
                    <td className={TD}>
                      <span className={`${CHIP} ${stateStyle(ticket)}`}>
                        {titleCase(ticket.state)}
                      </span>
                    </td>
                    <td className={`${TD} text-neutral-400`}>
                      {ticket.updatedAt.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <RaiseTicketModal
        show={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => load()}
      />
    </div>
  );
};
